import { Patient } from "@/api/dtos";
import ConsentDialogContext, { ConsentDialogState } from "@/contexts/ConsentDialogContext";
import { Chip, styled } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { IInstitution } from "@ngt/opms";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo } from "react";

const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const useConsentTableColumns = (institutions: IInstitution[] | null | undefined, showInitials: boolean, canConsentAdminister: boolean) => {
    const { setPatientStudyNumber, setDialogState } = useContext(ConsentDialogContext);

    const onRefuseClick = useCallback((studyNumber: string | null) => {
        setPatientStudyNumber(studyNumber);
        setDialogState(ConsentDialogState.Refuse);
    }, [setPatientStudyNumber, setDialogState]);

    const columns = useMemo<(GridColDef<Patient> | boolean)[]>(() => [
        {
            field: 'studyNumber',
            headerName: 'Temp. ID',
            minWidth: 150
        },
        showInitials && {
            field: 'initials',
            headerName: 'Initials'
        },
        {
            field: "institutionCode",
            headerName: 'Site Code',
            renderCell: rowData => (
                <StyledChip
                    label={rowData.row.institutionCode}
                    size="small"
                />
            ),
        },
        {
            field: 'institutionId',
            headerName: 'Site Name',
            valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
            flex: 1
        },
        {
            field: "enteredDate",
            headerName: "Date Entered",
            valueFormatter: (_, row) => {
                const date = row.enteredDate 
                    ? DateTime.fromISO(row.enteredDate) 
                    : undefined;

                return date?.toFormat("dd/MM/yyyy")
            },
            minWidth: 150
        },
    ], [institutions, showInitials, canConsentAdminister, onRefuseClick]);

    return columns.filter(x => !!x) as GridColDef<Patient>[];
}

export default useConsentTableColumns;
